import { Add, Remove, SearchOutlined, Star } from "@mui/icons-material";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Typography
} from "@mui/material";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ChipTabs from "../../../../components/ChipsTabs";
import { GET_RESOURCES } from "../../../../enums/ApiPaths";
import { ASSIGN_RESOURCE_TO_SERVICE } from "../../../../enums/QueryKeys";
import { useFetch } from "../../../../hooks/Fetching";
import usePrimaryColor from "../../../../hooks/usePrimaryColor";
import useUser from "../../../../hooks/useUser";
import { trimText } from "../../../../utils/Functions";
import {
  SelectedItemsPanel
} from "../../../admin/clinic/components/warehouse/components/services/components/ServiceResources";
import { useGetClinicWarehouseItems } from "../../../admin/clinic/components/warehouse/hooks/useGetClinicWarehouseItems";
import { useGetWarehousePreferences } from "../../../admin/clinic/components/warehouse/hooks/useGetWarehousePreferences";
import { formatCurrency } from "../../../admin/clinic/components/warehouse/utils/stockStatus";
import useAppointmentGeneral from "../hooks/useAppointmentGeneral";

const SEARCH_LOADER_DELAY_MS = 500;
const VISIBLE_ROWS = 5;
const TOUCH_TARGET_SIZE = 44;

export default function ResourcesManager() {
  const { setValue, getValues } = useFormContext();

  const { data, isLoading, error } = useAppointmentGeneral();

  const clinicService = data?.appointment?.priceList;
  const existingResources = data?.appointment?.resources ?? [];
  const seeded = React.useRef(false);

  React.useEffect(() => {
    if (seeded.current || isLoading) return;
    const current = getValues("selectedItems");
    if (!current || current.size === 0) {
      setValue(
        "selectedItems",
        new Map(
          existingResources.map((item) => [
            item.id,
            { ...item, quantity: item.quantity ?? 1 },
          ]),
        ),
      );
    }
    seeded.current = true;
  }, [isLoading, existingResources, getValues, setValue]);

  if (isLoading) return <LinearProgress />;

  return <ResourcesManagerContent clinicService={clinicService} />;
}

function ResourcesManagerContent({ clinicService }) {
  const sidebar = (
    <Stack spacing={2} sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }}>
      <SelectedItemsPanel showQuantity />
    </Stack>
  );

  if (clinicService == null) {
    return (
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Warehouse />
        {sidebar}
      </Stack>
    );
  }

  const content = [
    <Recommended clinicService={clinicService} />,
    <Warehouse />,
  ];
  const labels = ["Recommended", "Warehouse"];
  const icons = [<Star />, <WarehouseIcon />];
  const tabTestIds = [
    "appointment.finish-meeting.resources.tab.recommended",
    "appointment.finish-meeting.resources.tab.warehouse",
  ];

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
      <ChipTabs
        tabContent={content}
        tabLabels={labels}
        icons={icons}
        tabTestIds={tabTestIds}
      />
      {sidebar}
    </Stack>
  );
}

function Recommended({ clinicService = null }) {
  const { watch, setValue } = useFormContext();
  const { t } = useTranslation();
  const selectedItems = watch("selectedItems") ?? new Map();
  const [page, setPage] = React.useState(0);

  const url = GET_RESOURCES + clinicService?.id;
  const { data, isLoading } = useFetch(
    url,
    null,
    ASSIGN_RESOURCE_TO_SERVICE + clinicService?.id,
    clinicService?.id,
  );

  React.useEffect(() => {
    setPage(0);
  }, [clinicService?.id]);

  const sortedData = React.useMemo(
    () =>
      data
        ? [...data].sort((a, b) =>
            (a.title ?? "").localeCompare(b.title ?? "", undefined, {
              sensitivity: "base",
            }),
          )
        : [],
    [data],
  );

  if (isLoading) return <LinearProgress />;

  const handleQuantityChange = (item, quantity) => {
    const next = new Map(selectedItems);
    if (quantity <= 0) {
      next.delete(item.id);
    } else {
      next.set(item.id, { ...item, quantity });
    }
    setValue("selectedItems", next, { shouldDirty: true });
  };

  const handleSelectAll = () => {
    const next = new Map(selectedItems);
    sortedData.forEach((item) => {
      const existing = next.get(item.id);
      next.set(item.id, { ...item, quantity: existing?.quantity ?? 1 });
    });
    setValue("selectedItems", next, { shouldDirty: true });
  };

  if (!sortedData || sortedData.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{ width: { xs: "100%", md: 750 }, maxWidth: "100%", borderRadius: 2, overflow: "hidden" }}
      >
        <Typography color="text.secondary" align="center">
          {t("NoResourcesForService")}
        </Typography>
      </Paper>
    );
  }

  const totalElements = sortedData.length;
  const pageStart = page * VISIBLE_ROWS;
  const pagedItems = sortedData.slice(pageStart, pageStart + VISIBLE_ROWS);
  const allSelected = sortedData.every((item) => selectedItems.has(item.id));

  const handleChangePage = (_e, newPage) => setPage(newPage);

  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: "100%", md: 750 },
        maxWidth: "100%",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 1.5,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={handleSelectAll}
          disabled={allSelected}
          data-testid="appointment.finish-meeting.resources.recommended.select-all-button"
        >
          {t("SelectAll")}
        </Button>
      </Box>

      <Stack sx={{ minHeight: VISIBLE_ROWS * 56 }}>
        {pagedItems.map((item) => (
          <WarehouseItemRow
            key={item.id}
            item={item}
            quantity={selectedItems.get(item.id)?.quantity ?? 0}
            onQuantityChange={(qty) => handleQuantityChange(item, qty)}
          />
        ))}
      </Stack>

      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={VISIBLE_ROWS}
        rowsPerPageOptions={[VISIBLE_ROWS]}
        labelRowsPerPage=""
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          ".MuiTablePagination-toolbar": { minHeight: 40, pl: 1.5 },
          ".MuiTablePagination-selectLabel, .MuiTablePagination-select": {
            display: "none",
          },
        }}
      />
    </Paper>
  );
}

function Warehouse({ searchLabel = "Search" }) {
  const { currentClinic } = useUser();
  const { watch, setValue } = useFormContext();
  const selectedItems = watch("selectedItems") ?? new Map();

  const [page, setPage] = React.useState(0);
  const [searchInput, setSearchInput] = React.useState("");
  const [showLoader, setShowLoader] = React.useState(false);
  const { t } = useTranslation();

  const {
    data,
    isLoading,
    isFetching: isItemsFetching,
  } = useGetClinicWarehouseItems(currentClinic?.id, {
    categoryId: null,
    supplierId: null,
    search: searchInput,
    sort: null,
    page,
    size: VISIBLE_ROWS,
  });

  React.useEffect(() => {
    if (!isItemsFetching) {
      setShowLoader(false);
      return;
    }
    const timer = setTimeout(() => setShowLoader(true), SEARCH_LOADER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isItemsFetching]);

  const items = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;

  const handleQuantityChange = (item, quantity) => {
    const next = new Map(selectedItems);
    if (quantity <= 0) {
      next.delete(item.id);
    } else {
      next.set(item.id, { ...item, quantity });
    }
    setValue("selectedItems", next, { shouldDirty: true });
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    setPage(0);
  };

  const handleChangePage = (_e, newPage) => setPage(newPage);

  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: "100%", md: 750 },
        maxWidth: "100%",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          label={searchLabel}
          value={searchInput}
          onChange={handleSearchChange}
          inputProps={{
            "data-testid": "appointment.finish-meeting.resources.warehouse.search-input",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined
                  fontSize="small"
                  sx={{ color: "text.disabled" }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ position: "relative", minHeight: 3 }}>
        {showLoader && (
          <LinearProgress
            sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
          />
        )}
      </Box>

      <Stack sx={{ minHeight: VISIBLE_ROWS * 56 }}>
        {items.map((item) => (
          <WarehouseItemRow
            key={item.id}
            item={item}
            quantity={selectedItems.get(item.id)?.quantity ?? 0}
            onQuantityChange={(qty) => handleQuantityChange(item, qty)}
          />
        ))}

        {!isLoading && items.length === 0 && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("NoItemsMatchYourSearch")}
            </Typography>
          </Box>
        )}
      </Stack>

      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={VISIBLE_ROWS}
        rowsPerPageOptions={[VISIBLE_ROWS]}
        labelRowsPerPage=""
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          ".MuiTablePagination-toolbar": {
            minHeight: 40,
            pl: { xs: 0.5, sm: 1.5 },
          },
          ".MuiTablePagination-selectLabel, .MuiTablePagination-select": {
            display: "none",
          },
          ".MuiTablePagination-actions": { ml: { xs: 0, sm: 1 } },
        }}
      />
    </Paper>
  );
}

function WarehouseItemRow({ item, quantity, onQuantityChange }) {
  const { t } = useTranslation();
  const { currentClinic } = useUser();
  const { data: warehousePreferences } = useGetWarehousePreferences(currentClinic?.id);
  const currency = warehousePreferences?.currency ?? "EUR";
  const hasDiscount =
    item.discountedPriceWithTax != null &&
    item.priceWithTax != null &&
    item.discountedPriceWithTax < item.priceWithTax;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1.5}
      sx={{
        px: 1.5,
        py: 1,
        minHeight: 56,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: quantity > 0 ? "action.selected" : "transparent",
        transition: "background-color 0.15s ease",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={600} noWrap title={item.title}>
          {trimText(item.title, 20)}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="baseline">
          {hasDiscount && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ textDecoration: "line-through" }}
            >
              {formatCurrency(item.priceWithTax, currency)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {formatCurrency(item.discountedPriceWithTax ?? item.priceWithTax, currency)}
            {item.quantity != null
              ? ` · ${item.quantity} ${t("InStock")}`
              : null}
          </Typography>
        </Stack>
      </Box>

      <QuantityControl
        quantity={quantity}
        maxQuantity={item.quantity}
        onChange={onQuantityChange}
        testIdPrefix={`appointment.finish-meeting.resources.item.${item.id}`}
      />
    </Stack>
  );
}

function QuantityControl({ quantity, maxQuantity, onChange, testIdPrefix }) {
  const { t } = useTranslation();
  const atMax = false;
  const clamp = (value) => Math.max(0, Math.min(Infinity, value));

  if (quantity <= 0) {
    return (
      <Button
        variant="outlined"
        onClick={() => onChange(clamp(1))}
        startIcon={<Add />}
        sx={{
          minHeight: TOUCH_TARGET_SIZE,
          minWidth: 88,
          borderRadius: 2,
          flexShrink: 0,
        }}
        data-testid={`${testIdPrefix}.add-button`}
      >
        {t("Add")}
      </Button>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        flexShrink: 0,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 0.25,
      }}
    >
      <IconButton
        onClick={() => onChange(clamp(quantity - 1))}
        sx={{ width: TOUCH_TARGET_SIZE, height: TOUCH_TARGET_SIZE }}
        data-testid={`${testIdPrefix}.decrement-button`}
      >
        <Remove />
      </IconButton>
      <Typography
        variant="body1"
        fontWeight={600}
        sx={{ minWidth: 24, textAlign: "center" }}
        data-testid={`${testIdPrefix}.quantity-value`}
      >
        {quantity}
      </Typography>
      <IconButton
        onClick={() => onChange(clamp(quantity + 1))}
        sx={{ width: TOUCH_TARGET_SIZE, height: TOUCH_TARGET_SIZE }}
        data-testid={`${testIdPrefix}.increment-button`}
      >
        <Add />
      </IconButton>
    </Stack>
  );
}

function PriceViewer() {
  const color = usePrimaryColor();
  const { watch } = useFormContext();
  const { currentClinic } = useUser();
  const { data: warehousePreferences } = useGetWarehousePreferences(currentClinic?.id);
  const currency = warehousePreferences?.currency ?? "EUR";
  const selectedItems = watch("selectedItems") ?? new Map();

  const total = Array.from(selectedItems.values()).reduce(
    (sum, item) =>
      sum +
      (item.discountedPriceWithTax ?? item.priceWithTax ?? 0) * item.quantity,
    0,
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="body2">Final Price:</Typography>
      <Typography color={color} variant="h5" fontWeight="bold">
        {formatCurrency(total, currency)}
      </Typography>
    </Paper>
  );
}
